import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiFetch } from "./api";
import OtpInput from "./OtpInput";

/**
 * Authenticator-app (TOTP) enrolment: fetch a secret + QR, confirm with a code,
 * then reveal 10 one-time backup codes. When `forced`, there is no way out until
 * activation succeeds (used to gate admins/superadmins into setting up 2FA).
 */
const TwoFactorSetup: React.FC<{
  forced?: boolean;
  onDone: () => void;
  onCancel?: () => void;
}> = ({ forced = false, onDone, onCancel }) => {
  const { t } = useTranslation();
  const [qr, setQr] = useState("");
  const [secret, setSecret] = useState("");
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch("/api/2fa/totp/setup", { method: "POST" });
        const data = await res!.json();
        if (!res!.ok) {
          setError(data.error || t("twofaSetup.couldNotStart"));
        } else {
          setQr(data.qrDataUrl);
          setSecret(data.secret);
        }
      } catch {
        setError(t("twofaSetup.couldNotStart"));
      }
      setLoading(false);
    })();
  }, [t]);

  const activate = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await apiFetch("/api/2fa/totp/activate", {
        method: "POST",
        body: JSON.stringify({ code: code.trim() }),
      });
      const data = await res!.json();
      if (!res!.ok) {
        setError(data.error || t("twofaSetup.invalidCode"));
        setLoading(false);
        return;
      }
      setBackupCodes(data.backupCodes);
    } catch {
      setError(t("twofaSetup.activationFailed"));
    }
    setLoading(false);
  };

  const downloadBackup = () => {
    const blob = new Blob(
      [`${t("twofaSetup.backupFileHeader")}${backupCodes!.join("\n")}\n`],
      { type: "text/plain" },
    );
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "reviewcounter-backup-codes.txt";
    a.click();
  };

  // NB: this wrapper is a plain function, not a component, so it doesn't remount
  // its children (and drop input focus) on every keystroke.
  const card = (children: React.ReactNode) => (
    <div className={forced ? "min-h-screen flex items-center justify-center bg-stone-50 p-4" : ""}>
      <div className="bg-white rounded-xl shadow p-6 w-full max-w-md space-y-4">{children}</div>
    </div>
  );

  if (backupCodes) {
    return card(
      <>
        <h3 className="text-lg font-bold text-green-700">{t("twofaSetup.onTitle")}</h3>
        <p className="text-sm text-gray-600">
          {t("twofaSetup.saveCodesHint")}
        </p>
        <div className="grid grid-cols-2 gap-2 font-mono text-sm bg-stone-50 rounded p-3">
          {backupCodes.map((c) => (
            <span key={c}>{c}</span>
          ))}
        </div>
        <div className="flex gap-2">
          <button onClick={downloadBackup} className="flex-1 py-2 rounded border font-semibold">
            {t("twofaSetup.download")}
          </button>
          <button
            onClick={() => navigator.clipboard?.writeText(backupCodes.join("\n"))}
            className="flex-1 py-2 rounded border font-semibold"
          >
            {t("twofaSetup.copy")}
          </button>
        </div>
        <button
          onClick={onDone}
          className="w-full bg-teal-600 text-white py-2 rounded font-semibold"
        >
          {t("twofaSetup.savedContinue")}
        </button>
      </>,
    );
  }

  return card(
    <>
      <h3 className="text-lg font-bold text-gray-900">{t("twofaSetup.setUpTitle")}</h3>
      {forced && (
        <p className="text-sm text-amber-700 bg-amber-50 rounded p-2">
          {t("twofaSetup.roleRequires")}
        </p>
      )}
      {loading && !qr ? (
        <p className="text-sm text-gray-500">{t("twofaSetup.loadingShort")}</p>
      ) : (
        <>
          <p className="text-sm text-gray-600">
            {t("twofaSetup.scanHint")}
          </p>
          {qr && <img src={qr} alt={t("twofaSetup.qrAlt")} className="mx-auto w-44 h-44" />}
          {secret && (
            <p className="text-xs text-center text-gray-500">
              {t("twofaSetup.cantScan")} <span className="font-mono">{secret}</span>
            </p>
          )}
          <form onSubmit={activate} className="space-y-3">
            <OtpInput value={code} onChange={setCode} disabled={loading} />
            {error && <div className="text-sm text-center text-red-600">{error}</div>}
            <div className="flex gap-2">
              {!forced && onCancel && (
                <button
                  type="button"
                  onClick={onCancel}
                  className="flex-1 py-2 rounded border font-semibold text-gray-700"
                >
                  {t("twofaSetup.cancel")}
                </button>
              )}
              <button
                type="submit"
                disabled={loading || code.trim().length < 6}
                className="flex-1 bg-green-600 text-white py-2 rounded font-semibold disabled:opacity-50"
              >
                {loading ? t("twofaSetup.verifying") : t("twofaSetup.turnOn2fa")}
              </button>
            </div>
          </form>
        </>
      )}
    </>,
  );
};

export default TwoFactorSetup;
